import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDialog } from '../contexts/DialogContext';
import presupuestoService from '../services/presupuestoService';
import clienteService from '../services/clienteService';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './Presupuestos.css';

// Componente de select con búsqueda
const SearchableSelect = ({ value, onChange, options, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    option.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.toLowerCase?.().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    onChange(option.value || option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const displayValue = options.find(opt => (opt.value || opt) === value)?.label || 
                       options.find(opt => opt === value) || 
                       placeholder;

  return (
    <div className="searchable-select" ref={wrapperRef}>
      <div 
        className={`searchable-select-input ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar..."
            autoFocus
            disabled={disabled}
          />
        ) : (
          <span className={!value ? 'placeholder' : ''}>{displayValue}</span>
        )}
        <span className="searchable-select-arrow">▼</span>
      </div>
      {isOpen && !disabled && (
        <div className="searchable-select-dropdown">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const optionValue = option.value || option;
              const optionLabel = option.label || option;
              return (
                <div
                  key={index}
                  className={`searchable-select-option ${optionValue === value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  {optionLabel}
                </div>
              );
            })
          ) : (
            <div className="searchable-select-option disabled">No se encontraron resultados</div>
          )}
        </div>
      )}
    </div>
  );
};

const normalizarEntradaDecimal = (valor) => String(valor ?? '').trim().replace(',', '.');

const parsearCantidadAdicional = (valor, aceptaDecimales) => {
  const normalizado = normalizarEntradaDecimal(valor);

  if (!normalizado) return null;

  if (aceptaDecimales) {
    if (!/^\d*\.?\d+$/.test(normalizado)) return null;
    const numero = Number(normalizado);
    return Number.isFinite(numero) && numero > 0 ? numero : null;
  }

  if (!/^\d+$/.test(normalizado)) return null;
  const entero = parseInt(normalizado, 10);
  return Number.isInteger(entero) && entero >= 1 ? entero : null;
};

const Presupuestos = () => {
  const { user } = useAuth();
  const { showAlert, showConfirm, showPrompt } = useDialog();
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal crear presupuesto
  const [modalAbierto, setModalAbierto] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [configuracion, setConfiguracion] = useState(null);
  const [creando, setCreando] = useState(false);
  const [modalCargando, setModalCargando] = useState(false);
  const [presupuestoEditandoId, setPresupuestoEditandoId] = useState(null);
  
  // Modal ver presupuesto
  const [modalVerAbierto, setModalVerAbierto] = useState(false);
  const [presupuestoSeleccionado, setPresupuestoSeleccionado] = useState(null);
  
  // Estados de paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [itemsPorPagina, setItemsPorPagina] = useState(10);

  // Buscador de presupuestos por nombre y/o apellido del cliente (sin importar mayusculas ni tildes).
  const [busquedaCliente, setBusquedaCliente] = useState('');
  
  // Estado del presupuesto
  const [nuevoPresupuesto, setNuevoPresupuesto] = useState({
    clienteId: '',
    sucursal: user?.rol === 'admin' ? '' : (user?.sucursal || ''), // Admin elige, usuarios usan su sucursal
    formaPago: '',
    productos: [],
    observaciones: '',
    fleteTipo: 'porcentaje',
    fleteValor: 5,
    descuentoTipo: 'monto',
    descuentoValor: 0
  });

  // Producto temporal para agregar
  const [productoTemp, setProductoTemp] = useState({
    categoria: '',
    tipo: '',
    modelo: '',
    medidas: '',
    madera: '',
    tapizado: '',
    cantidadTapizado: '',
    lustre: '',
    color: '',
    cantidad: 1,
    // Campos específicos para DECORACION
    sku: '',
    proveedor: '',
    costo: 0,
    // Campos específicos para ADICIONALES
    tipoValor: '',
    aplicaFactores: false,
    aceptaDecimales: false
  });

  const [productoEspecial, setProductoEspecial] = useState({
    descripcion: '',
    precio: '',
    cantidad: 1
  });

  const clientesPorSucursal = useMemo(() => {
    return clientes.filter((cliente) => {
      if (!nuevoPresupuesto.sucursal) return false;
      return cliente.sucursal === nuevoPresupuesto.sucursal;
    });
  }, [clientes, nuevoPresupuesto.sucursal]);

  const clienteIdSeleccionado = nuevoPresupuesto.clienteId;
  const clienteSeleccionadoSigueVisible = useMemo(() => {
    if (!clienteIdSeleccionado) return true;
    return clientesPorSucursal.some((cliente) => cliente._id === clienteIdSeleccionado);
  }, [clientesPorSucursal, clienteIdSeleccionado]);

  // Cargar presupuestos
  const cargarPresupuestos = async () => {
    try {
      setLoading(true);
      const response = await presupuestoService.getPresupuestos();
      setPresupuestos(response.data || []);
      setPaginaActual(1); // Resetear a página 1 al recargar
    } catch (error) {
      console.error('Error cargando presupuestos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarPresupuestos();
  }, []);

  useEffect(() => {
    if (!clienteIdSeleccionado) return;

    if (!clienteSeleccionadoSigueVisible) {
      setNuevoPresupuesto((prev) => ({
        ...prev,
        clienteId: ''
      }));
    }
  }, [clienteIdSeleccionado, clienteSeleccionadoSigueVisible]);

  const cargarDatosModal = async () => {
    try {
      setModalCargando(true);

      const clientesPromise = clienteService.getClientes();
      const catalogoPromise = catalogo.length ? Promise.resolve(catalogo) : api.get('/catalogo');
      const configPromise = configuracion
        ? Promise.resolve(configuracion)
        : api.get('/configuracion').catch((configError) => {
            console.warn('No se pudo cargar configuración, se usarán factores por defecto:', configError);
            return null;
          });

      const [responseClientes, catalogoData, config] = await Promise.all([
        clientesPromise,
        catalogoPromise,
        configPromise
      ]);

      setClientes(responseClientes.data || []);
      setCatalogo(catalogoData || []);
      setConfiguracion(config || null);

      if (config?.porcentajeFlete !== undefined) {
        setNuevoPresupuesto((prev) => ({
          ...prev,
          fleteTipo: 'porcentaje',
          fleteValor: Number(config.porcentajeFlete) || 0
        }));
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setModalCargando(false);
    }
  };

  // Abrir modal y cargar clientes/catálogo/configuración
  const abrirModal = () => {
    setModalAbierto(true);
    cargarDatosModal();
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setPresupuestoEditandoId(null);
    setNuevoPresupuesto({
      clienteId: '',
      sucursal: user?.rol === 'admin' ? '' : (user?.sucursal || ''),
      formaPago: '',
      productos: [],
      observaciones: '',
      fleteTipo: 'porcentaje',
      fleteValor: Number(configuracion?.porcentajeFlete) || 5,
      descuentoTipo: 'monto',
      descuentoValor: 0
    });
    setProductoTemp({
      categoria: '',
      tipo: '',
      modelo: '',
      medidas: '',
      madera: '',
      tapizado: '',
      cantidadTapizado: '',
      lustre: '',
      color: '',
      cantidad: 1,
      sku: '',
      proveedor: '',
      costo: 0,
      tipoValor: '',
      aplicaFactores: false,
      aceptaDecimales: false
    });
    setProductoEspecial({ descripcion: '', precio: '', cantidad: 1 });
  };

  const abrirModalEditar = async (id) => {
    try {
      setModalAbierto(true);
      setPresupuestoEditandoId(id);
      setModalCargando(true);

      const clientesPromise = clienteService.getClientes();
      const catalogoPromise = catalogo.length ? Promise.resolve(catalogo) : api.get('/catalogo');
      const configPromise = configuracion
        ? Promise.resolve(configuracion)
        : api.get('/configuracion').catch((configError) => {
            console.warn('No se pudo cargar configuración, se usarán factores por defecto:', configError);
            return null;
          });
      const presupuestoPromise = presupuestoService.getPresupuestoById(id);

      const [responseClientes, catalogoData, config, responsePresupuesto] = await Promise.all([
        clientesPromise,
        catalogoPromise,
        configPromise,
        presupuestoPromise
      ]);

      setClientes(responseClientes.data || []);
      setCatalogo(catalogoData || []);
      setConfiguracion(config || null);

      const presupuesto = responsePresupuesto.data;
      const productosMapeados = (presupuesto?.productos || []).map((p) => {
        const variante = p.varianteSeleccionada || {};
        return {
          catalogoRef: p.catalogoRef?._id || p.catalogoRef,
          categoria: variante.categoria || '',
          tipo: variante.tipo || '',
          modelo: variante.modelo || '',
          medidas: variante.medidas || '',
          madera: variante.madera || '',
          tapizado: variante.tapizado || '',
          cantidadTapizado: variante.cantidadTapizado || '',
          lustre: variante.lustre || '',
          color: variante.color || '',
          sku: variante.sku || '',
          proveedor: variante.proveedor || '',
          tipoValor: variante.tipoValor || '',
          aplicaFactores: !!variante.aplicaFactores,
          aceptaDecimales: !!variante.aceptaDecimales,
          cantidad: Number(p.cantidad) || 1,
          costo: Number(p.costoBase ?? variante.costo) || 0,
          precio: Number(p.precioUnitario) || 0,
          subtotal: Number(p.subtotal) || 0,
          fabricaManual: !!p.fabricaManual
        };
      });

      setNuevoPresupuesto({
        clienteId: presupuesto?.cliente?._id || presupuesto?.cliente || '',
        sucursal: presupuesto?.sucursal || (user?.rol === 'admin' ? '' : (user?.sucursal || '')),
        formaPago: presupuesto?.formaPago || '',
        productos: productosMapeados,
        observaciones: presupuesto?.observaciones || '',
        fleteTipo: presupuesto?.fleteTipo || 'porcentaje',
        fleteValor: Number(presupuesto?.fleteValor ?? config?.porcentajeFlete) || 0,
        descuentoTipo: presupuesto?.descuentoTipo || 'monto',
        descuentoValor: Number(presupuesto?.descuentoValor) || 0
      });

      setProductoTemp({
        categoria: '',
        tipo: '',
        modelo: '',
        medidas: '',
        madera: '',
        tapizado: '',
        cantidadTapizado: '',
        lustre: '',
        color: '',
        cantidad: 1,
        sku: '',
        proveedor: '',
        costo: 0,
        tipoValor: '',
        aplicaFactores: false,
        aceptaDecimales: false
      });
    } catch (error) {
      console.error('Error preparando edición de presupuesto:', error);
      setModalAbierto(false);
      setPresupuestoEditandoId(null);
      showAlert('No se pudo abrir la edición del presupuesto', { type: 'error' });
    } finally {
      setModalCargando(false);
    }
  };

  // Calcular precio según sucursal y forma de pago.
  // Para DECORACION se usa factor por sucursal, independiente de forma de pago.
  const calcularPrecio = (costo, sucursal, formaPago, categoria = '') => {
    const categoriaNormalizada = (categoria || '').toUpperCase();
    const factorDecoracionDefaults = {
      parana: 2.2,
      esperanza: 2.2,
      rosario: 2.3
    };

    if (categoriaNormalizada === 'DECORACION') {
      const factorConfigurado = Number(configuracion?.coeficientesSucursales?.[sucursal]?.factorDecoracion);
      const factor = Number.isFinite(factorConfigurado) && factorConfigurado > 0
        ? factorConfigurado
        : (factorDecoracionDefaults[sucursal] || factorDecoracionDefaults.parana);
      return Math.round((Number(costo) || 0) * factor);
    }

    const defaults = {
      parana: { contadoEfectivo: 1.8, transferencia: 1.8, tarjeta: 1.8, ivaTransferencia: 1.21, ivaTarjeta: 1.21, recargoTarjeta1: 1.1, recargoTarjeta2: 1.15 },
      esperanza: { contadoEfectivo: 1.8, transferencia: 1.8, tarjeta: 1.8, ivaTransferencia: 1.21, ivaTarjeta: 1.21, recargoTarjeta1: 1.1, recargoTarjeta2: 1.15 },
      rosario: { contadoEfectivo: 1.9, transferencia: 1.9, tarjeta: 1.8, ivaTransferencia: 1.21, ivaTarjeta: 1.21, recargoTarjeta1: 1.15, recargoTarjeta2: 1.15 }
    };

    const coef = configuracion?.coeficientesSucursales?.[sucursal] || defaults[sucursal] || defaults.parana;
    const iva = Number(coef.iva ?? coef.ivaTransferencia ?? coef.ivaTarjeta) || 0;

    let precio = Number(costo) || 0;

    if (formaPago === 'contado_efectivo') {
      precio = precio * (Number(coef.contadoEfectivo) || 0);
    } else if (formaPago === 'transferencia') {
      precio = precio * (Number(coef.transferencia) || 0) * iva;
    } else if (formaPago === 'tarjeta') {
      precio = precio * (Number(coef.tarjeta) || 0) * iva * (Number(coef.recargoTarjeta1) || 0) * (Number(coef.recargoTarjeta2) || 0);
    }

    return Math.round(precio);
  };

  const obtenerOpcionesCascada = () => {
    const categorias = [...new Set(catalogo.map(c => c.categoria))];

    let catalogoFiltrado = catalogo;
    if (productoTemp.categoria) {
      catalogoFiltrado = catalogoFiltrado.filter(c => c.categoria === productoTemp.categoria);
    }
    
    // Para ADICIONALES, solo necesitamos modelos (sin cascada compleja)
    if (productoTemp.categoria === 'ADICIONALES') {
      const modelos = [...new Set(catalogoFiltrado.map(c => c.modelo))];
      return { 
        categorias, 
        tipos: [], 
        modelos, 
        medidas: [], 
        maderas: [], 
        tapizados: [], 
        cantidadesTapizado: [], 
        lustres: [], 
        skus: [], 
        proveedores: [] 
      };
    }

    const tipos = [...new Set(catalogoFiltrado.map(c => c.tipo))];

    if (productoTemp.tipo) {
      catalogoFiltrado = catalogoFiltrado.filter(c => c.tipo === productoTemp.tipo);
    }
    const modelos = [...new Set(catalogoFiltrado.map(c => c.modelo))];

    // MEDIDAS - Para MUEBLES, RESPALDO y SOFA (no SILLA)
    let medidas = [];
    if (productoTemp.categoria !== 'SILLA' && productoTemp.modelo) {
      const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
      if (itemsCatalogo.length > 0) {
        const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
        medidas = [...new Set(todasLasVariantes.map(v => v.medidas).filter(Boolean))];
      }
    }

    // MADERAS - Para MUEBLES, RESPALDO y SILLA (no SOFA)
    let maderas = [];
    if (productoTemp.categoria !== 'SOFA') {
      if (productoTemp.categoria === 'SILLA') {
        // SILLA: solo filtrar por modelo
        if (productoTemp.modelo) {
          const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
          if (itemsCatalogo.length > 0) {
            const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
            maderas = [...new Set(todasLasVariantes.map(v => v.madera).filter(Boolean))];
          }
        }
      } else {
        // MUEBLES y RESPALDO: filtrar por modelo y medidas
        if (productoTemp.modelo && productoTemp.medidas) {
          const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
          if (itemsCatalogo.length > 0) {
            const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
            maderas = [...new Set(
              todasLasVariantes
                .filter(v => v.medidas === productoTemp.medidas)
                .map(v => v.madera)
                .filter(Boolean)
            )];
          }
        }
      }
    }

    // TAPIZADOS - Para RESPALDO y SILLA
    let tapizados = [];
    if (productoTemp.categoria === 'RESPALDO') {
      if (productoTemp.modelo && productoTemp.medidas && productoTemp.madera) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          tapizados = [...new Set(
            todasLasVariantes
              .filter(v => v.medidas === productoTemp.medidas && v.madera === productoTemp.madera)
              .map(v => v.tapizado)
              .filter(Boolean)
          )];
        }
      }
    } else if (productoTemp.categoria === 'SILLA') {
      if (productoTemp.modelo && productoTemp.madera) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          tapizados = [...new Set(
            todasLasVariantes
              .filter(v => v.madera === productoTemp.madera)
              .map(v => v.tapizado)
              .filter(Boolean)
          )];
        }
      }
    }

    // CANTIDADES TAPIZADO - Para RESPALDO, SILLA y SOFA
    let cantidadesTapizado = [];
    if (productoTemp.categoria === 'RESPALDO') {
      if (productoTemp.modelo && productoTemp.medidas && productoTemp.madera && productoTemp.tapizado) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          cantidadesTapizado = [...new Set(
            todasLasVariantes
              .filter(v => 
                v.medidas === productoTemp.medidas && 
                v.madera === productoTemp.madera &&
                v.tapizado === productoTemp.tapizado
              )
              .map(v => v.cantidadTapizado)
              .filter(Boolean)
          )];
        }
      }
    } else if (productoTemp.categoria === 'SILLA') {
      if (productoTemp.modelo && productoTemp.madera && productoTemp.tapizado) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          cantidadesTapizado = [...new Set(
            todasLasVariantes
              .filter(v => v.madera === productoTemp.madera && v.tapizado === productoTemp.tapizado)
              .map(v => v.cantidadTapizado)
              .filter(Boolean)
          )];
        }
      }
    } else if (productoTemp.categoria === 'SOFA') {
      if (productoTemp.modelo && productoTemp.medidas) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          cantidadesTapizado = [...new Set(
            todasLasVariantes
              .filter(v => v.medidas === productoTemp.medidas)
              .map(v => v.cantidadTapizado)
              .filter(Boolean)
          )];
        }
      }
    }

    // LUSTRES - Según categoría
    let lustres = [];
    if (productoTemp.categoria === 'SOFA') {
      // SOFA: filtrar por medidas y cantidadTapizado
      if (productoTemp.modelo && productoTemp.medidas && productoTemp.cantidadTapizado) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          lustres = [...new Set(
            todasLasVariantes
              .filter(v => v.medidas === productoTemp.medidas && v.cantidadTapizado === productoTemp.cantidadTapizado)
              .map(v => v.lustre)
              .filter(Boolean)
          )];
        }
      }
    } else if (productoTemp.categoria === 'RESPALDO') {
      // RESPALDO: filtrar por medidas, madera, tapizado y cantidadTapizado
      if (productoTemp.modelo && productoTemp.medidas && productoTemp.madera && productoTemp.tapizado && productoTemp.cantidadTapizado) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          lustres = [...new Set(
            todasLasVariantes
              .filter(v => 
                v.medidas === productoTemp.medidas && 
                v.madera === productoTemp.madera &&
                v.tapizado === productoTemp.tapizado &&
                v.cantidadTapizado === productoTemp.cantidadTapizado
              )
              .map(v => v.lustre)
              .filter(Boolean)
          )];
        }
      }
    } else if (productoTemp.categoria === 'SILLA') {
      // SILLA: filtrar por madera, tapizado y cantidadTapizado
      if (productoTemp.modelo && productoTemp.madera && productoTemp.tapizado && productoTemp.cantidadTapizado) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          lustres = [...new Set(
            todasLasVariantes
              .filter(v => 
                v.madera === productoTemp.madera &&
                v.tapizado === productoTemp.tapizado &&
                v.cantidadTapizado === productoTemp.cantidadTapizado
              )
              .map(v => v.lustre)
              .filter(Boolean)
          )];
        }
      }
    } else {
      // MUEBLES: filtrar por medidas y madera
      if (productoTemp.modelo && productoTemp.medidas && productoTemp.madera) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          lustres = [...new Set(
            todasLasVariantes
              .filter(v => v.medidas === productoTemp.medidas && v.madera === productoTemp.madera)
              .map(v => v.lustre)
              .filter(Boolean)
          )];
        }
      }
    }

    // SKUS y PROVEEDORES - Solo para DECORACION
    let skus = [];
    let proveedores = [];
    if (productoTemp.categoria === 'DECORACION') {
      if (productoTemp.modelo) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === productoTemp.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          skus = [...new Set(todasLasVariantes.map(v => v.sku).filter(Boolean))];
          proveedores = [...new Set(todasLasVariantes.map(v => v.proveedor).filter(Boolean))];
        }
      } else if (catalogoFiltrado.length > 0) {
        const todasLasVariantes = catalogoFiltrado.flatMap(item => item.variantes || []);
        skus = [...new Set(todasLasVariantes.map(v => v.sku).filter(Boolean))];
        proveedores = [...new Set(todasLasVariantes.map(v => v.proveedor).filter(Boolean))];
      }
    }

    return { categorias, tipos, modelos, medidas, maderas, tapizados, cantidadesTapizado, lustres, skus, proveedores };
  };

  const obtenerClaveProducto = (producto) => {
    return [
      String(producto.catalogoRef || ''),
      producto.categoria || '',
      producto.tipo || '',
      producto.modelo || '',
      producto.medidas || '',
      producto.madera || '',
      producto.tapizado || '',
      producto.cantidadTapizado || '',
      producto.lustre || '',
      producto.color || '',
      producto.sku || '',
      producto.proveedor || ''
    ].join('|');
  };

  const agregarProductoAlPresupuesto = (productoNuevo) => {
    setNuevoPresupuesto((prev) => {
      const productosActuales = Array.isArray(prev.productos) ? prev.productos : [];

      // ADICIONALES se mantienen como líneas separadas para no alterar su lógica.
      if (productoNuevo.categoria === 'ADICIONALES') {
        return {
          ...prev,
          productos: [...productosActuales, productoNuevo]
        };
      }

      const claveNuevo = obtenerClaveProducto(productoNuevo);
      const indexExistente = productosActuales.findIndex((p) => obtenerClaveProducto(p) === claveNuevo);

      if (indexExistente === -1) {
        return {
          ...prev,
          productos: [...productosActuales, productoNuevo]
        };
      }

      const productosActualizados = [...productosActuales];
      const existente = productosActualizados[indexExistente];
      const nuevaCantidad = (Number(existente.cantidad) || 0) + (Number(productoNuevo.cantidad) || 0);

      productosActualizados[indexExistente] = {
        ...existente,
        cantidad: nuevaCantidad,
        subtotal: Math.round((Number(existente.precio) || 0) * nuevaCantidad)
      };

      return {
        ...prev,
        productos: productosActualizados
      };
    });
  };

  // Agregar producto al presupuesto
  const agregarProducto = () => {
    // Caso especial para DECORACION
    if (productoTemp.categoria === 'DECORACION') {
      // Validar campos requeridos para DECORACION
      if (!productoTemp.modelo || !productoTemp.sku || !nuevoPresupuesto.sucursal || !nuevoPresupuesto.formaPago) {
        return;
      }

      // Buscar en el catálogo
      const itemCatalogo = catalogo.find(c =>
        c.categoria === 'DECORACION' &&
        c.modelo === productoTemp.modelo
      );

      if (!itemCatalogo) return;

      // Buscar variante con el SKU
      const variante = itemCatalogo.variantes.find(v => v.sku === productoTemp.sku);
      if (!variante) return;

      const costo = variante.costo;
      const precio = calcularPrecio(costo, nuevoPresupuesto.sucursal, nuevoPresupuesto.formaPago, 'DECORACION');
      const subtotal = precio * productoTemp.cantidad;

      const producto = {
        catalogoRef: itemCatalogo._id,
        categoria: 'DECORACION',
        tipo: 'DECORACION',
        modelo: productoTemp.modelo,
        sku: productoTemp.sku,
        proveedor: variante.proveedor,
        cantidad: productoTemp.cantidad,
        costo: costo,
        precio: precio,
        subtotal: subtotal,
        fabricaManual: false
      };

      agregarProductoAlPresupuesto(producto);

      // Limpiar producto temporal
      setProductoTemp({
        categoria: '',
        tipo: '',
        modelo: '',
        medidas: '',
        madera: '',
        tapizado: '',
        cantidadTapizado: '',
        lustre: '',
        cantidad: 1,
        sku: '',
        proveedor: '',
        costo: 0,
        tipoValor: '',
        aplicaFactores: false,
        aceptaDecimales: false
      });

      return;
    }

    // Caso especial para ADICIONALES
    if (productoTemp.categoria === 'ADICIONALES') {
      // Validar campos requeridos para ADICIONALES
      if (!productoTemp.modelo || !nuevoPresupuesto.sucursal || !nuevoPresupuesto.formaPago) {
        return;
      }

      // Buscar en el catálogo
      const itemCatalogo = catalogo.find(c =>
        c.categoria === 'ADICIONALES' &&
        c.modelo === productoTemp.modelo
      );

      if (!itemCatalogo) return;

      // ADICIONALES solo tiene una variante
      const variante = itemCatalogo.variantes[0];
      if (!variante) return;

      const cantidadAdicional = parsearCantidadAdicional(
        productoTemp.cantidad,
        variante.aceptaDecimales === true
      );

      if (!cantidadAdicional) {
        showAlert(
          variante.aceptaDecimales
            ? 'Cantidad invalida. Puede usar decimales (ej: 0.8 o 0,8).'
            : 'Cantidad invalida. Este adicional solo permite numeros enteros.',
          { type: 'warning' }
        );
        return;
      }

      let costoBase = variante.costo;
      let precio = costoBase;

      // Si aplicaFactores, calcular el precio según la sucursal
      if (variante.aplicaFactores) {
        precio = calcularPrecio(costoBase, nuevoPresupuesto.sucursal, nuevoPresupuesto.formaPago);
      }

      // Si es PORCENTUAL, no calculamos el subtotal ahora (se calculará al final)
      // Si es MONETARIO, el subtotal es el precio
      const subtotal = variante.tipoValor === 'MONETARIO' ? Math.round(precio * cantidadAdicional) : 0;

      const producto = {
        catalogoRef: itemCatalogo._id,
        categoria: productoTemp.categoria,
        tipo: '', // Para ADICIONALES, tipo vacío (solo mostraremos el modelo)
        modelo: productoTemp.modelo,
        cantidad: cantidadAdicional,
        costo: costoBase,
        precio: precio,
        subtotal: subtotal,
        // Campos específicos de ADICIONALES
        tipoValor: variante.tipoValor,
        aplicaFactores: variante.aplicaFactores,
        aceptaDecimales: !!variante.aceptaDecimales,
        fabricaManual: false
      };

      agregarProductoAlPresupuesto(producto);

      // Limpiar producto temporal
      setProductoTemp({
        categoria: '',
        tipo: '',
        modelo: '',
        medidas: '',
        madera: '',
        tapizado: '',
        cantidadTapizado: '',
        lustre: '',
        cantidad: 1,
        sku: '',
        proveedor: '',
        costo: 0,
        tipoValor: '',
        aplicaFactores: false,
        aceptaDecimales: false
      });

      return;
    }

    // Flujo normal para MUEBLES, SILLAS, SOFAS, etc.
    // Validar campos según categoría
    const camposRequeridos = [productoTemp.categoria, productoTemp.tipo, productoTemp.modelo, productoTemp.lustre];
    
    if (productoTemp.categoria === 'SOFA') {
      camposRequeridos.push(productoTemp.medidas, productoTemp.cantidadTapizado);
    } else if (productoTemp.categoria === 'RESPALDO') {
      camposRequeridos.push(productoTemp.medidas, productoTemp.madera, productoTemp.tapizado, productoTemp.cantidadTapizado);
    } else if (productoTemp.categoria === 'SILLA') {
      camposRequeridos.push(productoTemp.madera, productoTemp.tapizado, productoTemp.cantidadTapizado);
    } else { // MUEBLES
      camposRequeridos.push(productoTemp.medidas, productoTemp.madera);
    }

    if (camposRequeridos.some(campo => !campo) || !nuevoPresupuesto.sucursal || !nuevoPresupuesto.formaPago) {
      return;
    }

    // Buscar el item del catálogo
    const itemsCatalogo = catalogo.filter(c =>
      c.categoria === productoTemp.categoria &&
      c.tipo === productoTemp.tipo &&
      c.modelo === productoTemp.modelo
    );

    if (itemsCatalogo.length === 0) return;

    // Buscar variante según categoría
    let variante = null;
    let catalogoRefId = null;
    for (const itemCatalogo of itemsCatalogo) {
      let varianteEncontrada;
      
      if (productoTemp.categoria === 'SOFA') {
        varianteEncontrada = itemCatalogo.variantes.find(v =>
          v.medidas === productoTemp.medidas &&
          v.cantidadTapizado === productoTemp.cantidadTapizado &&
          v.lustre === productoTemp.lustre
        );
      } else if (productoTemp.categoria === 'SILLA') {
        varianteEncontrada = itemCatalogo.variantes.find(v =>
          v.madera === productoTemp.madera &&
          v.tapizado === productoTemp.tapizado &&
          v.cantidadTapizado === productoTemp.cantidadTapizado &&
          v.lustre === productoTemp.lustre
        );
      } else if (productoTemp.categoria === 'RESPALDO') {
        varianteEncontrada = itemCatalogo.variantes.find(v =>
          v.medidas === productoTemp.medidas &&
          v.madera === productoTemp.madera &&
          v.tapizado === productoTemp.tapizado &&
          v.cantidadTapizado === productoTemp.cantidadTapizado &&
          v.lustre === productoTemp.lustre
        );
      } else { // MUEBLES
        varianteEncontrada = itemCatalogo.variantes.find(v =>
          v.medidas === productoTemp.medidas &&
          v.madera === productoTemp.madera &&
          v.lustre === productoTemp.lustre
        );
      }
      
      if (varianteEncontrada) {
        variante = varianteEncontrada;
        catalogoRefId = itemCatalogo._id;
        break;
      }
    }

    if (!variante) return;

    const costo = variante.costo;
    const precio = calcularPrecio(costo, nuevoPresupuesto.sucursal, nuevoPresupuesto.formaPago);
    const subtotal = precio * productoTemp.cantidad;

    const producto = {
      catalogoRef: catalogoRefId,
      categoria: productoTemp.categoria,
      tipo: productoTemp.tipo,
      modelo: productoTemp.modelo,
      cantidad: productoTemp.cantidad,
      costo: costo,
      precio: precio,
      subtotal: subtotal,
      fabricaManual: false
    };

    // Agregar campos según categoría
    if (productoTemp.categoria === 'SOFA') {
      producto.medidas = productoTemp.medidas;
      producto.cantidadTapizado = productoTemp.cantidadTapizado;
      producto.lustre = productoTemp.lustre;
    } else if (productoTemp.categoria === 'SILLA') {
      producto.madera = productoTemp.madera;
      producto.tapizado = productoTemp.tapizado;
      producto.cantidadTapizado = productoTemp.cantidadTapizado;
      producto.lustre = productoTemp.lustre;
      producto.color = productoTemp.color;
    } else if (productoTemp.categoria === 'RESPALDO') {
      producto.medidas = productoTemp.medidas;
      producto.madera = productoTemp.madera;
      producto.tapizado = productoTemp.tapizado;
      producto.cantidadTapizado = productoTemp.cantidadTapizado;
      producto.lustre = productoTemp.lustre;
      producto.color = productoTemp.color;
    } else { // MUEBLES
      producto.medidas = productoTemp.medidas;
      producto.madera = productoTemp.madera;
      producto.lustre = productoTemp.lustre;
      producto.color = productoTemp.color;
    }

    agregarProductoAlPresupuesto(producto);

    // Limpiar producto temporal
    setProductoTemp({
      categoria: '',
      tipo: '',
      modelo: '',
      medidas: '',
      madera: '',
      tapizado: '',
      cantidadTapizado: '',
      lustre: '',
      color: '',
      cantidad: 1,
      sku: '',
      proveedor: '',
      costo: 0,
      tipoValor: '',
      aplicaFactores: false,
      aceptaDecimales: false
    });
  };

  const agregarProductoEspecial = () => {
    const descripcion = productoEspecial.descripcion.trim();
    const precio = Number(String(productoEspecial.precio).replace(',', '.'));
    const cantidad = Math.max(1, parseInt(productoEspecial.cantidad, 10) || 1);

    if (!descripcion) {
      showAlert('Ingresá una descripción para el producto especial.', { type: 'warning' });
      return;
    }

    if (!Number.isFinite(precio) || precio < 0) {
      showAlert('Ingresá un precio válido para el producto especial.', { type: 'warning' });
      return;
    }

    agregarProductoAlPresupuesto({
      categoria: 'ESPECIAL',
      tipo: '',
      modelo: descripcion,
      cantidad,
      costo: precio,
      precio,
      subtotal: Math.round(precio * cantidad),
      fabricaManual: false
    });

    setProductoEspecial({ descripcion: '', precio: '', cantidad: 1 });
  };

  // Eliminar producto del presupuesto
  const eliminarProducto = (index) => {
    const nuevosProductos = nuevoPresupuesto.productos.filter((_, i) => i !== index);
    setNuevoPresupuesto({
      ...nuevoPresupuesto,
      productos: nuevosProductos
    });
  };

  const actualizarCantidadProducto = (index, cantidadIngresada) => {
    setNuevoPresupuesto((prev) => {
      const productosActualizados = [...prev.productos];
      const productoActual = productosActualizados[index];
      if (!productoActual) return prev;

      if (productoActual.categoria === 'ADICIONALES') {
        const cantidadAdicional = parsearCantidadAdicional(
          cantidadIngresada,
          productoActual.aceptaDecimales === true
        );

        if (!cantidadAdicional) {
          return prev;
        }

        productosActualizados[index] = {
          ...productoActual,
          cantidad: cantidadAdicional,
          subtotal: productoActual.tipoValor === 'MONETARIO'
            ? Math.round((Number(productoActual.precio) || 0) * cantidadAdicional)
            : 0
        };

        return {
          ...prev,
          productos: productosActualizados
        };
      }

      const cantidad = Math.max(1, parseInt(cantidadIngresada, 10) || 1);

      productosActualizados[index] = {
        ...productoActual,
        cantidad,
        subtotal: Math.round((Number(productoActual.precio) || 0) * cantidad)
      };

      return {
        ...prev,
        productos: productosActualizados
      };
    });
  };

  const actualizarAjuste = (campo, valor) => {
    setNuevoPresupuesto({
      ...nuevoPresupuesto,
      [campo]: campo.endsWith('Valor')
        ? (valor === '' ? '' : Math.max(0, Number(valor) || 0))
        : valor
    });
  };

  // Calcular totales
  const calcularTotales = () => {
    // Separar productos normales de ADICIONALES de tipo PORCENTUAL
    const productosNormales = nuevoPresupuesto.productos.filter(p => 
      p.categoria !== 'ADICIONALES' || p.tipoValor === 'MONETARIO'
    );
    const adicionalesPorcentaje = nuevoPresupuesto.productos.filter(p => 
      p.categoria === 'ADICIONALES' && p.tipoValor === 'PORCENTUAL'
    );

    // Calcular subtotal de productos normales (incluye ADICIONALES MONETARIOS)
    const subtotalBase = productosNormales.reduce((total, p) => total + p.subtotal, 0);

    // Calcular ADICIONALES de tipo PORCENTUAL sobre el subtotal base
    const totalAdicionales = adicionalesPorcentaje.reduce((total, p) => {
      const porcentaje = p.precio / 100; // precio ya tiene el factor aplicado si corresponde
      return total + (subtotalBase * porcentaje);
    }, 0);

    const subtotalConAdicionales = subtotalBase + totalAdicionales;

    const descuento = nuevoPresupuesto.descuentoTipo === 'porcentaje'
      ? Math.round(subtotalConAdicionales * ((Number(nuevoPresupuesto.descuentoValor) || 0) / 100))
      : Math.round(Number(nuevoPresupuesto.descuentoValor) || 0);

    const descuentoAplicado = Math.min(descuento, subtotalConAdicionales);
    const baseFlete = Math.max(0, subtotalConAdicionales - descuentoAplicado);

    const flete = nuevoPresupuesto.fleteTipo === 'porcentaje'
      ? Math.round(baseFlete * ((Number(nuevoPresupuesto.fleteValor) || 0) / 100))
      : Math.round(Number(nuevoPresupuesto.fleteValor) || 0);

    const total = Math.max(0, subtotalConAdicionales - descuentoAplicado + flete);
    
    return { subtotal: subtotalBase, totalAdicionales, descuento: descuentoAplicado, flete, total };
  };

  // Crear o editar presupuesto
  const handleGuardarPresupuesto = async () => {
    console.log('🔍 handleGuardarPresupuesto llamado');
    console.log('📋 Datos:', nuevoPresupuesto);
    
    if (!nuevoPresupuesto.clienteId || !nuevoPresupuesto.sucursal || 
        !nuevoPresupuesto.formaPago || nuevoPresupuesto.productos.length === 0) {
      console.log('❌ Validación falló:', {
        clienteId: nuevoPresupuesto.clienteId,
        sucursal: nuevoPresupuesto.sucursal,
        formaPago: nuevoPresupuesto.formaPago,
        productosLength: nuevoPresupuesto.productos.length
      });
      return;
    }

    try {
      setCreando(true);

      const { subtotal, totalAdicionales, descuento, flete, total } = calcularTotales();

      const presupuestoData = {
        cliente: nuevoPresupuesto.clienteId,
        sucursal: nuevoPresupuesto.sucursal,
        formaPago: nuevoPresupuesto.formaPago,
        productos: nuevoPresupuesto.productos.map(p => ({
          catalogoRef: p.catalogoRef,
          cantidad: p.cantidad,
          costoBase: p.costo,
          precioUnitario: p.precio,
          subtotal: p.subtotal,
          fabricaManual: p.fabricaManual || false,
          varianteSeleccionada: {
            categoria: p.categoria,
            tipo: p.tipo,
            modelo: p.modelo,
            madera: p.madera,
            lustre: p.lustre,
            color: p.color,
            medidas: p.medidas,
            tapizado: p.tapizado,
            cantidadTapizado: p.cantidadTapizado,
            sku: p.sku,
            proveedor: p.proveedor,
            tipoValor: p.tipoValor,
            aplicaFactores: p.aplicaFactores,
            aceptaDecimales: p.aceptaDecimales,
            costo: p.costo
          }
        })),
        observaciones: nuevoPresupuesto.observaciones,
        adicionales: totalAdicionales,
        descuentoTipo: nuevoPresupuesto.descuentoTipo,
        descuentoValor: Number(nuevoPresupuesto.descuentoValor) || 0,
        descuento: descuento,
        fleteTipo: nuevoPresupuesto.fleteTipo,
        fleteValor: Number(nuevoPresupuesto.fleteValor) || 0,
        subtotal: subtotal,
        flete: flete,
        total: total
      };

      if (presupuestoEditandoId) {
        await presupuestoService.updatePresupuesto(presupuestoEditandoId, presupuestoData);
      } else {
        await presupuestoService.createPresupuesto(presupuestoData);
      }
      cerrarModal();
      cargarPresupuestos();
    } catch (error) {
      console.error('Error guardando presupuesto:', error);
      showAlert(error?.message || 'No se pudo guardar el presupuesto', { type: 'error' });
    } finally {
      setCreando(false);
    }
  };

  // Aceptar presupuesto y convertir a pedido
  const handleAceptarPresupuesto = async (id) => {
    try {
      // PASO 1: Confirmar aceptación
      const confirmado = await showConfirm('¿Está seguro de aceptar este presupuesto? Se creará un pedido automáticamente.', {
        title: 'Aceptar presupuesto',
        confirmLabel: 'Aceptar',
      });
      if (!confirmado) {
        return;
      }

      // PASO 2: Aceptar el presupuesto — el backend verifica stock real y determina tipoPedido
      const response = await presupuestoService.aceptarPresupuesto(id);

      const tipoPedido = response.data?.tipoPedido;
      const pedidoId = response.data?.pedido?._id;

      // PASO 3: Si el pedido requiere fabricación (lo sabe el backend tras verificar stock),
      //         pedir diasLimite y actualizar el pedido recién creado
      if ((tipoPedido === 'a_fabrica' || tipoPedido === 'mixto') && pedidoId) {
        const tipoLabel = tipoPedido === 'mixto' ? 'MIXTO (fábrica + stock)' : 'A FÁBRICA';
        const diasLimiteStr = await showPrompt(
          `El pedido fue creado como ${tipoLabel}.\n¿En cuántos días debe estar listo?`,
          { title: 'Plazo de fabricación', defaultValue: '30', placeholder: '30', confirmLabel: 'Guardar' }
        );

        if (diasLimiteStr !== null) {
          const diasLimite = parseInt(diasLimiteStr);
          if (!isNaN(diasLimite) && diasLimite >= 1) {
            await api.put(`/pedidos/${pedidoId}/dias-limite`, { diasLimite });
          }
        }
      }

      // PASO 4: Mostrar mensaje de resultado
      let mensaje = response.message || 'Presupuesto aceptado';

      if (response.data?.productosAutoCambiados && response.data.productosAutoCambiados.length > 0) {
        const productosLista = response.data.productosAutoCambiados
          .map(p => `- ${p.modelo} (${p.categoria})`)
          .join('\n');
        mensaje += `\n\n⚠ PRODUCTOS AUTO-CAMBIADOS A FÁBRICA POR FALTA DE STOCK:\n${productosLista}`;
      }

      await showAlert(mensaje, { type: response.data?.productosAutoCambiados?.length > 0 ? 'warning' : 'success', title: 'Presupuesto aceptado' });
      cargarPresupuestos();

    } catch (error) {
      console.error('Error aceptando presupuesto:', error);
      showAlert('Error al aceptar presupuesto: ' + (error.response?.data?.message || error.message), { type: 'error' });
    }
  };

  // Ver presupuesto
  const handleVerPresupuesto = async (id) => {
    try {
      const response = await presupuestoService.getPresupuestoById(id);
      setPresupuestoSeleccionado(response.data);
      setModalVerAbierto(true);
    } catch (error) {
      console.error('Error cargando presupuesto:', error);
    }
  };

  // Descargar PDF de presupuesto
  const handleDescargarPresupuestoPDF = async (presupuesto) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showAlert('No hay token de autenticación. Por favor, inicie sesión nuevamente.', { type: 'error' });
        return;
      }

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/presupuestos/${presupuesto._id}/pdf`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errorMessage = 'Error al generar PDF de presupuesto';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch {
          errorMessage = `Error del servidor: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Presupuesto_${presupuesto._id.slice(-8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error descargando PDF de presupuesto:', error);
      showAlert(`Error al descargar el PDF: ${error.message}`, { type: 'error' });
    }
  };

  // Eliminar presupuesto
  const handleEliminarPresupuesto = async (id) => {
    const confirmado = await showConfirm('¿Está seguro de eliminar este presupuesto? Esta acción no se puede deshacer.', {
      title: 'Eliminar presupuesto',
      confirmLabel: 'Eliminar',
    });
    if (!confirmado) {
      return;
    }

    try {
      await presupuestoService.deletePresupuesto(id);
      cargarPresupuestos();
    } catch (error) {
      console.error('Error eliminando presupuesto:', error);
    }
  };

  const normalizarTextoBusqueda = (texto) => String(texto ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

  const presupuestosFiltrados = useMemo(() => {
    const terminos = normalizarTextoBusqueda(busquedaCliente).split(/\s+/).filter(Boolean);
    if (terminos.length === 0) return presupuestos;
    return presupuestos.filter((presupuesto) => {
      const nombreCompleto = normalizarTextoBusqueda(presupuesto.cliente?.nombre || '');
      const apellidoCompleto = normalizarTextoBusqueda(presupuesto.cliente?.apellido || '');
      const nombreYApellido = `${nombreCompleto} ${apellidoCompleto}`.trim();
      return terminos.every((termino) => nombreYApellido.includes(termino));
    });
  }, [presupuestos, busquedaCliente]);

  useEffect(() => {
    setPaginaActual(1);
  }, [busquedaCliente]);

  if (loading) {
    return <LoadingSpinner label="Cargando presupuestos..." />;
  }

  const totalPaginas = Math.ceil(presupuestosFiltrados.length / itemsPorPagina);
  const indexInicio = (paginaActual - 1) * itemsPorPagina;
  const indexFin = indexInicio + itemsPorPagina;
  const presupuestosPaginados = presupuestosFiltrados.slice(indexInicio, indexFin);

  // Función para ir a una página específica
  const irAPagina = (pagina) => {
    if (pagina >= 1 && pagina <= totalPaginas) {
      setPaginaActual(pagina);
    }
  };

  const { subtotal, totalAdicionales, descuento, flete, total } = calcularTotales();

  const formatearEtiquetaAjuste = (nombre, tipo, valor) => {
    if (tipo === 'porcentaje') {
      return `${nombre} (${Number(valor) || 0}%):`;
    }
    return `${nombre}:`;
  };

  return (
    <div className="presupuestos-container">
      <div className="presupuestos-header">
        <h1>📋 Presupuestos</h1>
        <button className="btn-primary" onClick={abrirModal}>+ Nuevo Presupuesto</button>
      </div>

      <div className="presupuestos-filtros" style={{ marginBottom: '12px', maxWidth: '420px' }}>
        <input
          type="text"
          className="input-busqueda"
          placeholder="Buscar por nombre y/o apellido..."
          value={busquedaCliente}
          onChange={(e) => setBusquedaCliente(e.target.value)}
        />
      </div>

      <div className="presupuestos-tabla">
        <table>
          <thead>
            <tr>
              <th>N°</th>
              <th>CLIENTE</th>
              <th>SUCURSAL</th>
              <th>FORMA DE PAGO</th>
              <th>TOTAL</th>
              <th>ESTADO</th>
              <th>FECHA</th>
              <th>ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {presupuestosPaginados.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center">
                  No hay presupuestos creados
                </td>
              </tr>
            ) : (
              presupuestosPaginados.map((presupuesto, index) => (
                <tr key={presupuesto._id}>
                  <td>{indexInicio + index + 1}</td>
                  <td>{presupuesto.cliente?.nombre || '-'}</td>
                  <td>{presupuesto.sucursal?.toUpperCase() || '-'}</td>
                  <td>{presupuesto.formaPago?.replace('_', ' ').toUpperCase() || '-'}</td>
                  <td>${presupuesto.total?.toLocaleString() || 0}</td>
                  <td>
                    <span className={`estado-badge estado-${presupuesto.estado}`}>
                      {presupuesto.estado?.toUpperCase()}
                    </span>
                  </td>
                  <td>{new Date(presupuesto.createdAt).toLocaleDateString()}</td>
                  <td>
                    {presupuesto.estado === 'pendiente' && (
                      <button 
                        className="btn-success" 
                        title="Aceptar Presupuesto"
                        onClick={() => handleAceptarPresupuesto(presupuesto._id)}
                      >
                        ✓ Aceptar
                      </button>
                    )}
                    {presupuesto.estado !== 'aprobado' && (
                      <button
                        className="btn-edit"
                        title="Editar"
                        onClick={() => abrirModalEditar(presupuesto._id)}
                      >
                        ✏️
                      </button>
                    )}
                    <button 
                      className="btn-edit" 
                      title="Ver"
                      onClick={() => handleVerPresupuesto(presupuesto._id)}
                    >
                      👁️
                    </button>
                    <button
                      className="btn-edit btn-pdf-icon"
                      title="Generar PDF"
                      onClick={() => handleDescargarPresupuestoPDF(presupuesto)}
                    >
                      📄
                    </button>
                    <button 
                      className="btn-delete" 
                      title="Eliminar"
                      onClick={() => handleEliminarPresupuesto(presupuesto._id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Controles de paginación */}
      {presupuestos.length > 0 && (
        <div className="paginacion-container">
          <div className="paginacion-info">
            Mostrando {indexInicio + 1} - {Math.min(indexFin, presupuestos.length)} de {presupuestos.length} presupuestos
          </div>
          
          <div className="paginacion-controles">
            <button 
              onClick={() => irAPagina(1)}
              disabled={paginaActual === 1}
              className="btn-paginacion"
              title="Primera página"
            >
              ««
            </button>
            
            <button 
              onClick={() => irAPagina(paginaActual - 1)}
              disabled={paginaActual === 1}
              className="btn-paginacion"
              title="Página anterior"
            >
              ‹
            </button>
            
            <div className="paginacion-paginas">
              {Array.from({ length: totalPaginas }, (_, i) => i + 1)
                .filter(pagina => {
                  // Mostrar solo páginas cercanas a la actual
                  return pagina === 1 || 
                         pagina === totalPaginas || 
                         (pagina >= paginaActual - 2 && pagina <= paginaActual + 2);
                })
                .map((pagina, index, array) => (
                  <React.Fragment key={pagina}>
                    {index > 0 && array[index - 1] !== pagina - 1 && (
                      <span className="paginacion-ellipsis">...</span>
                    )}
                    <button
                      onClick={() => irAPagina(pagina)}
                      className={`btn-paginacion ${paginaActual === pagina ? 'active' : ''}`}
                    >
                      {pagina}
                    </button>
                  </React.Fragment>
                ))
              }
            </div>
            
            <button 
              onClick={() => irAPagina(paginaActual + 1)}
              disabled={paginaActual === totalPaginas}
              className="btn-paginacion"
              title="Página siguiente"
            >
              ›
            </button>
            
            <button 
              onClick={() => irAPagina(totalPaginas)}
              disabled={paginaActual === totalPaginas}
              className="btn-paginacion"
              title="Última página"
            >
              »»
            </button>
          </div>

          <div className="paginacion-selector">
            <label>Items por página:</label>
            <select 
              value={itemsPorPagina} 
              onChange={(e) => {
                setItemsPorPagina(Number(e.target.value));
                setPaginaActual(1);
              }}
              className="select-items-pagina"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}

      {/* Modal crear presupuesto */}
      {modalAbierto && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content modal-presupuesto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{presupuestoEditandoId ? 'Editar Presupuesto' : 'Crear Nuevo Presupuesto'}</h2>
              <button className="modal-close" onClick={cerrarModal}>×</button>
            </div>
            
            <div className="modal-body">
              {modalCargando && (
                <LoadingSpinner label="Cargando clientes, catalogo y configuracion..." inline />
              )}

              {/* Datos del presupuesto */}
              <div className="presupuesto-datos">
                {/* Campo de sucursal */}
                {user?.rol === 'admin' ? (
                  <div className="form-group">
                    <label>SUCURSAL *</label>
                    <SearchableSelect
                      value={nuevoPresupuesto.sucursal}
                      onChange={(value) => setNuevoPresupuesto({ ...nuevoPresupuesto, sucursal: value, clienteId: '' })}
                      options={[
                        { value: 'parana', label: 'PARANÁ' },
                        { value: 'esperanza', label: 'ESPERANZA' },
                        { value: 'rosario', label: 'ROSARIO' }
                      ]}
                      placeholder="Seleccione sucursal"
                      disabled={creando}
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <label>SUCURSAL</label>
                    <div style={{ padding: '10px', background: '#f0f0f0', borderRadius: '6px', fontWeight: '500' }}>
                      {user?.sucursal?.toUpperCase() || 'SIN SUCURSAL'}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>CLIENTE *</label>
                  <SearchableSelect
                    value={nuevoPresupuesto.clienteId}
                    onChange={(value) => setNuevoPresupuesto({ ...nuevoPresupuesto, clienteId: value })}
                    options={clientesPorSucursal.map(cliente => ({ value: cliente._id, label: cliente.nombre }))}
                    placeholder={nuevoPresupuesto.sucursal ? 'Seleccione cliente' : 'Seleccione primero una sucursal'}
                    disabled={creando || !nuevoPresupuesto.sucursal}
                  />
                </div>

                <div className="form-group">
                  <label>FORMA DE PAGO *</label>
                  <SearchableSelect
                    value={nuevoPresupuesto.formaPago}
                    onChange={(value) => setNuevoPresupuesto({ ...nuevoPresupuesto, formaPago: value })}
                    options={[
                      { value: 'contado_efectivo', label: 'CONTADO EFECTIVO' },
                      { value: 'transferencia', label: 'TRANSFERENCIA' },
                      { value: 'tarjeta', label: 'TARJETA' }
                    ]}
                    placeholder="Seleccione forma de pago"
                    disabled={creando}
                  />
                </div>
              </div>

              {/* Productos del catálogo */}
              <div className="agregar-producto-section">
                <h3>Productos del Catálogo</h3>
                
                <div className="producto-form-grid">
                  <div className="form-group">
                    <label>CATEGORÍA</label>
                    <SearchableSelect
                      value={productoTemp.categoria}
                      onChange={(value) => setProductoTemp({
                        ...productoTemp,
                        categoria: value,
                        tipo: '',
                        modelo: '',
                        medidas: '',
                        madera: '',
                        tapizado: '',
                        cantidadTapizado: '',
                        lustre: '',
                        color: '',
                        sku: '',
                        proveedor: '',
                        costo: 0,
                        tipoValor: '',
                        aplicaFactores: false,
                        aceptaDecimales: false
                      })}
                      options={obtenerOpcionesCascada().categorias}
                      placeholder="Seleccione"
                      disabled={!nuevoPresupuesto.sucursal || !nuevoPresupuesto.formaPago}
                    />
                  </div>

                  {productoTemp.categoria !== 'DECORACION' && productoTemp.categoria !== 'ADICIONALES' && (
                    <div className="form-group">
                      <label>TIPO</label>
                      <SearchableSelect
                        value={productoTemp.tipo}
                        onChange={(value) => setProductoTemp({
                          ...productoTemp,
                          tipo: value,
                          modelo: '',
                          medidas: '',
                          madera: '',
                          tapizado: '',
                          cantidadTapizado: '',
                          lustre: '',
                          color: '',
                          sku: '',
                          proveedor: '',
                          costo: 0
                        })}
                        options={obtenerOpcionesCascada().tipos}
                        placeholder="Seleccione"
                        disabled={!productoTemp.categoria}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>{productoTemp.categoria === 'ADICIONALES' ? 'NOMBRE' : 'MODELO'}</label>
                    <SearchableSelect
                      value={productoTemp.modelo}
                      onChange={(value) => {
                        // Para ADICIONALES, cargar automáticamente los datos de la variante
                        if (productoTemp.categoria === 'ADICIONALES') {
                          const itemCatalogo = catalogo.find(c => c.categoria === 'ADICIONALES' && c.modelo === value);
                          const variante = itemCatalogo?.variantes[0];
                          setProductoTemp({
                            ...productoTemp,
                            modelo: value,
                            tipoValor: variante?.tipoValor || '',
                            aplicaFactores: variante?.aplicaFactores || false,
                            aceptaDecimales: !!variante?.aceptaDecimales,
                            costo: variante?.costo || 0,
                            medidas: '',
                            madera: '',
                            tapizado: '',
                            cantidadTapizado: '',
                            lustre: '',
                            sku: '',
                            proveedor: ''
                          });
                        } else {
                          setProductoTemp({
                            ...productoTemp,
                            modelo: value,
                            medidas: '',
                            madera: '',
                            tapizado: '',
                            cantidadTapizado: '',
                            lustre: '',
                            color: '',
                            sku: '',
                            proveedor: '',
                            costo: 0,
                            tipoValor: '',
                            aplicaFactores: false,
                            aceptaDecimales: false
                          });
                        }
                      }}
                      options={obtenerOpcionesCascada().modelos}
                      placeholder="Seleccione"
                      disabled={(productoTemp.categoria === 'DECORACION' || productoTemp.categoria === 'ADICIONALES') ? !productoTemp.categoria : !productoTemp.tipo}
                    />
                  </div>

                  {/* Campos específicos para DECORACION */}
                  {productoTemp.categoria === 'DECORACION' && (
                    <>
                      <div className="form-group">
                        <label>SKU</label>
                        <SearchableSelect
                          value={productoTemp.sku}
                          onChange={(value) => {
                            const itemsDecoracion = catalogo.filter(c => c.categoria === 'DECORACION');

                            // Si ya hay modelo seleccionado, intentar primero dentro de ese modelo.
                            let itemCatalogo = null;
                            let variante = null;

                            if (productoTemp.modelo) {
                              itemCatalogo = itemsDecoracion.find(c => c.modelo === productoTemp.modelo);
                              variante = itemCatalogo?.variantes?.find(v => v.sku === value);
                            }

                            // Fallback: buscar SKU en todo DECORACION.
                            if (!variante) {
                              for (const item of itemsDecoracion) {
                                const found = item.variantes?.find(v => v.sku === value);
                                if (found) {
                                  itemCatalogo = item;
                                  variante = found;
                                  break;
                                }
                              }
                            }

                            setProductoTemp(prev => ({
                              ...prev,
                              modelo: itemCatalogo?.modelo || prev.modelo,
                              sku: value,
                              proveedor: variante?.proveedor || '',
                              costo: variante?.costo || 0
                            }));
                          }}
                          options={obtenerOpcionesCascada().skus}
                          placeholder="Seleccione SKU"
                          disabled={!productoTemp.categoria}
                        />
                      </div>

                      <div className="form-group">
                        <label>PROVEEDOR</label>
                        <input
                          type="text"
                          value={productoTemp.proveedor}
                          disabled
                          placeholder="Auto-completado"
                          style={{backgroundColor: '#f0f0f0'}}
                        />
                      </div>

                    </>
                  )}

                  {/* MEDIDAS - Para MUEBLES, RESPALDO y SOFA (no SILLA, DECORACION ni ADICIONALES) */}
                  {(productoTemp.categoria === 'MUEBLES' || productoTemp.categoria === 'RESPALDO' || productoTemp.categoria === 'SOFA') && (
                    <div className="form-group">
                      <label>MEDIDAS</label>
                      <SearchableSelect
                        value={productoTemp.medidas}
                        onChange={(value) => setProductoTemp({
                          ...productoTemp,
                          medidas: value,
                          madera: '',
                          tapizado: '',
                          cantidadTapizado: '',
                          lustre: ''
                        })}
                        options={obtenerOpcionesCascada().medidas}
                        placeholder="Seleccione"
                        disabled={!productoTemp.modelo}
                      />
                    </div>
                  )}

                  {/* MADERA - Para MUEBLES, RESPALDO y SILLA (no SOFA, DECORACION ni ADICIONALES) */}
                  {productoTemp.categoria !== 'SOFA' && productoTemp.categoria !== 'DECORACION' && productoTemp.categoria !== 'ADICIONALES' && (
                    <div className="form-group">
                      <label>MADERA</label>
                      <SearchableSelect
                        value={productoTemp.madera}
                        onChange={(value) => setProductoTemp({
                          ...productoTemp,
                          madera: value,
                          tapizado: '',
                          cantidadTapizado: '',
                          lustre: ''
                        })}
                        options={obtenerOpcionesCascada().maderas}
                        placeholder="Seleccione"
                        disabled={
                          (productoTemp.categoria === 'SILLA' ? !productoTemp.modelo : !productoTemp.medidas)
                        }
                      />
                    </div>
                  )}

                  {/* TAPIZADO - Para RESPALDO y SILLA */}
                  {(productoTemp.categoria === 'RESPALDO' || productoTemp.categoria === 'SILLA') && (
                    <div className="form-group">
                      <label>TAPIZADO</label>
                      <SearchableSelect
                        value={productoTemp.tapizado}
                        onChange={(value) => setProductoTemp({
                          ...productoTemp,
                          tapizado: value,
                          cantidadTapizado: '',
                          lustre: ''
                        })}
                        options={obtenerOpcionesCascada().tapizados}
                        placeholder="Seleccione"
                        disabled={!productoTemp.madera}
                      />
                    </div>
                  )}

                  {/* CANTIDAD TAPIZADO - Para RESPALDO y SILLA */}
                  {(productoTemp.categoria === 'RESPALDO' || productoTemp.categoria === 'SILLA') && (
                    <div className="form-group">
                      <label>CANTIDAD</label>
                      <SearchableSelect
                        value={productoTemp.cantidadTapizado}
                        onChange={(value) => setProductoTemp({
                          ...productoTemp,
                          cantidadTapizado: value,
                          lustre: ''
                        })}
                        options={obtenerOpcionesCascada().cantidadesTapizado}
                        placeholder="Seleccione"
                        disabled={!productoTemp.tapizado}
                      />
                    </div>
                  )}

                  {/* CANT TELA/CUERO - Para SOFA */}
                  {productoTemp.categoria === 'SOFA' && (
                    <div className="form-group">
                      <label>CANT TELA/CUERO</label>
                      <SearchableSelect
                        value={productoTemp.cantidadTapizado}
                        onChange={(value) => setProductoTemp({
                          ...productoTemp,
                          cantidadTapizado: value,
                          lustre: ''
                        })}
                        options={obtenerOpcionesCascada().cantidadesTapizado}
                        placeholder="Seleccione"
                        disabled={!productoTemp.medidas}
                      />
                    </div>
                  )}

                  {/* LUSTRE para MUEBLES, RESPALDO, SILLA / MATERIAL para SOFA (no DECORACION ni ADICIONALES) */}
                  {productoTemp.categoria !== 'DECORACION' && productoTemp.categoria !== 'ADICIONALES' && (
                  <div className="form-group">
                    <label>{productoTemp.categoria === 'SOFA' ? 'MATERIAL' : 'LUSTRE'}</label>
                    <SearchableSelect
                      value={productoTemp.lustre}
                      onChange={(value) => setProductoTemp({
                        ...productoTemp,
                        lustre: value
                      })}
                      options={obtenerOpcionesCascada().lustres}
                      placeholder="Seleccione"
                      disabled={
                        (productoTemp.categoria === 'SOFA' ? !productoTemp.cantidadTapizado :
                         (productoTemp.categoria === 'RESPALDO' || productoTemp.categoria === 'SILLA') ? !productoTemp.cantidadTapizado :
                         !productoTemp.madera)
                      }
                    />
                  </div>
                  )}

                  {/* COLOR (del lustre) para MUEBLES, RESPALDO y SILLA (no SOFA, DECORACION ni ADICIONALES) */}
                  {(productoTemp.categoria === 'MUEBLES' || productoTemp.categoria === 'RESPALDO' || productoTemp.categoria === 'SILLA') && (
                  <div className="form-group">
                    <label>COLOR</label>
                    <input
                      type="text"
                      value={productoTemp.color}
                      onChange={(e) => setProductoTemp({
                        ...productoTemp,
                        color: e.target.value
                      })}
                      placeholder="Ej: Nogal oscuro"
                    />
                  </div>
                  )}

                  <div className="form-group">
                    <label>CANTIDAD</label>
                    {productoTemp.categoria === 'ADICIONALES' ? (
                      <input
                        type="text"
                        inputMode={productoTemp.aceptaDecimales ? 'decimal' : 'numeric'}
                        value={productoTemp.cantidad}
                        onChange={(e) => {
                          const valorRaw = e.target.value;
                          const valorNormalizado = normalizarEntradaDecimal(valorRaw);
                          const regex = productoTemp.aceptaDecimales ? /^\d*[.,]?\d*$/ : /^\d*$/;
                          if (valorRaw === '' || regex.test(valorRaw) || regex.test(valorNormalizado)) {
                            setProductoTemp({
                              ...productoTemp,
                              cantidad: valorRaw
                            });
                          }
                        }}
                        placeholder={productoTemp.aceptaDecimales ? 'Ej: 0.8 o 0,8' : 'Ej: 1'}
                      />
                    ) : (
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={productoTemp.cantidad}
                        onChange={(e) => setProductoTemp({
                          ...productoTemp,
                          cantidad: parseInt(e.target.value, 10) || 1
                        })}
                      />
                    )}
                  </div>

                  <div className="form-group">
                    <button 
                      className="btn-primary btn-agregar"
                      onClick={agregarProducto}
                      disabled={
                        productoTemp.categoria === 'DECORACION' 
                          ? !productoTemp.sku 
                          : productoTemp.categoria === 'ADICIONALES'
                          ? !productoTemp.modelo
                          : !productoTemp.lustre
                      }
                    >
                      + Agregar
                    </button>
                  </div>
                </div>
              </div>

              <div className="agregar-producto-section">
                <h3>Productos Especiales</h3>
                <div className="producto-form-grid">
                  <div className="form-group">
                    <label>DESCRIPCIÓN</label>
                    <input
                      type="text"
                      value={productoEspecial.descripcion}
                      onChange={(e) => setProductoEspecial({ ...productoEspecial, descripcion: e.target.value })}
                      placeholder="Ej: Mesa especial a medida"
                    />
                  </div>
                  <div className="form-group">
                    <label>PRECIO UNITARIO</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={productoEspecial.precio}
                      onChange={(e) => {
                        const valor = e.target.value;
                        if (valor === '' || /^\d*[.,]?\d*$/.test(valor)) {
                          setProductoEspecial({ ...productoEspecial, precio: valor });
                        }
                      }}
                      placeholder="Ej: 150000"
                    />
                  </div>
                  <div className="form-group">
                    <label>CANTIDAD</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={productoEspecial.cantidad}
                      onChange={(e) => setProductoEspecial({
                        ...productoEspecial,
                        cantidad: parseInt(e.target.value, 10) || 1
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <button
                      className="btn-primary btn-agregar"
                      onClick={agregarProductoEspecial}
                      disabled={!productoEspecial.descripcion.trim() || productoEspecial.precio === ''}
                    >
                      + Agregar
                    </button>
                  </div>
                </div>
              </div>

              {/* Lista de productos agregados */}
              {nuevoPresupuesto.productos.length > 0 && (
                <div className="productos-agregados">
                  <h3>Productos en el presupuesto</h3>
                  <div className="tabla-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>PRODUCTO</th>
                        <th>VARIANTE</th>
                        <th>CANT.</th>
                        <th>PRECIO UNIT.</th>
                        <th>SUBTOTAL</th>
                        <th>A FÁBRICA</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {nuevoPresupuesto.productos.map((p, index) => {
                        let variante = '';
                        let precioDisplay = `$${p.precio.toLocaleString()}`;
                        let subtotalDisplay = `$${p.subtotal.toLocaleString()}`;
                        
                        if (p.categoria === 'DECORACION') {
                          variante = `SKU: ${p.sku} - ${p.proveedor}`;
                        } else if (p.categoria === 'ESPECIAL') {
                          variante = 'FUERA DE CATÁLOGO';
                        } else if (p.categoria === 'ADICIONALES') {
                          variante = 'ADICIONAL';
                          
                          // Para porcentuales, el subtotal se calcula al final
                          if (p.tipoValor === 'PORCENTUAL') {
                            precioDisplay = `${p.precio}%`;
                            subtotalDisplay = 'Se calcula al final';
                          }
                        } else if (p.categoria === 'SOFA') {
                          variante = `${p.medidas} - ${p.cantidadTapizado} - ${p.lustre}`;
                        } else if (p.categoria === 'SILLA') {
                          variante = `${p.madera} - ${p.tapizado} - ${p.cantidadTapizado} - ${p.lustre}${p.color ? ` - ${p.color}` : ''}`;
                        } else if (p.categoria === 'RESPALDO') {
                          variante = `${p.medidas} - ${p.madera} - ${p.tapizado} - ${p.cantidadTapizado} - ${p.lustre}${p.color ? ` - ${p.color}` : ''}`;
                        } else { // MUEBLES
                          variante = `${p.medidas} - ${p.madera} - ${p.lustre}${p.color ? ` - ${p.color}` : ''}`;
                        }
                        
                        return (
                          <tr key={index}>
                            <td>{p.tipo ? `${p.tipo} ${p.modelo}` : p.modelo}</td>
                            <td>{variante}</td>
                            <td>
                              {p.categoria === 'ADICIONALES' ? (
                                <input
                                  type="text"
                                  inputMode={p.aceptaDecimales ? 'decimal' : 'numeric'}
                                  value={String(p.cantidad)}
                                  onChange={(e) => {
                                    const valorRaw = e.target.value;
                                    const valorNormalizado = normalizarEntradaDecimal(valorRaw);
                                    const regex = p.aceptaDecimales ? /^\d*[.,]?\d*$/ : /^\d*$/;
                                    if (valorRaw === '' || regex.test(valorRaw) || regex.test(valorNormalizado)) {
                                      actualizarCantidadProducto(index, valorRaw);
                                    }
                                  }}
                                  style={{ width: '64px', textAlign: 'center' }}
                                />
                              ) : (
                                <input
                                  type="number"
                                  min="1"
                                  value={p.cantidad}
                                  onChange={(e) => actualizarCantidadProducto(index, e.target.value)}
                                  style={{ width: '64px', textAlign: 'center' }}
                                />
                              )}
                            </td>
                            <td>{precioDisplay}</td>
                            <td>{subtotalDisplay}</td>
                            <td style={{ textAlign: 'center' }}>
                              {p.categoria === 'ESPECIAL' ? '-' : (
                                <input 
                                  type="checkbox" 
                                  checked={p.fabricaManual || false}
                                  onChange={(e) => {
                                    const nuevosProductos = [...nuevoPresupuesto.productos];
                                    nuevosProductos[index].fabricaManual = e.target.checked;
                                    setNuevoPresupuesto({ ...nuevoPresupuesto, productos: nuevosProductos });
                                  }}
                                  style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                />
                              )}
                            </td>
                            <td>
                              <button 
                                className="btn-delete-small"
                                onClick={() => eliminarProducto(index)}
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>

                  <div className="totales">
                    <div className="total-row">
                      <span>SUBTOTAL:</span>
                      <span>${subtotal.toLocaleString()}</span>
                    </div>
                    {totalAdicionales > 0 && (
                      <div className="total-row">
                        <span>ADICIONALES (%):</span>
                        <span>${totalAdicionales.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="total-row total-row-editable">
                      <span>BONIFICACION:</span>
                      <div className="ajuste-config">
                        <select
                          value={nuevoPresupuesto.descuentoTipo}
                          onChange={(e) => actualizarAjuste('descuentoTipo', e.target.value)}
                          className="ajuste-select"
                        >
                          <option value="monto">Monto fijo</option>
                          <option value="porcentaje">Porcentaje</option>
                        </select>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={nuevoPresupuesto.descuentoValor}
                          onChange={(e) => {
                            const valor = e.target.value.replace(',', '.');
                            if (valor === '' || /^\d*\.?\d*$/.test(valor)) {
                              actualizarAjuste('descuentoValor', valor);
                            }
                          }}
                          className="ajuste-input"
                        />
                      </div>
                    </div>
                    {descuento > 0 && (
                      <div className="total-row total-row-discount">
                        <span>{formatearEtiquetaAjuste('BONIFICACION', nuevoPresupuesto.descuentoTipo, nuevoPresupuesto.descuentoValor)}</span>
                        <span>-${descuento.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="total-row total-row-editable">
                      <span>FLETE:</span>
                      <div className="ajuste-config">
                        <select
                          value={nuevoPresupuesto.fleteTipo}
                          onChange={(e) => actualizarAjuste('fleteTipo', e.target.value)}
                          className="ajuste-select"
                        >
                          <option value="porcentaje">Porcentaje</option>
                          <option value="monto">Monto fijo</option>
                        </select>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={nuevoPresupuesto.fleteValor}
                          onChange={(e) => {
                            const valor = e.target.value.replace(',', '.');
                            if (valor === '' || /^\d*\.?\d*$/.test(valor)) {
                              actualizarAjuste('fleteValor', valor);
                            }
                          }}
                          className="ajuste-input"
                        />
                      </div>
                    </div>
                    <div className="total-row">
                      <span>{formatearEtiquetaAjuste('FLETE', nuevoPresupuesto.fleteTipo, nuevoPresupuesto.fleteValor)}</span>
                      <span>${flete.toLocaleString()}</span>
                    </div>
                    <div className="total-row total-final">
                      <span>TOTAL:</span>
                      <span>${total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>OBSERVACIONES</label>
                <textarea
                  value={nuevoPresupuesto.observaciones}
                  onChange={(e) => setNuevoPresupuesto({ ...nuevoPresupuesto, observaciones: e.target.value })}
                  rows="3"
                  placeholder="Observaciones adicionales..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={cerrarModal}
                disabled={creando}
              >
                Cancelar
              </button>
              <button 
                className="btn-primary" 
                onClick={handleGuardarPresupuesto}
                disabled={creando || nuevoPresupuesto.productos.length === 0}
              >
                {creando ? 'Guardando...' : (presupuestoEditandoId ? 'Guardar cambios' : 'Crear Presupuesto')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal ver presupuesto */}
      {modalVerAbierto && presupuestoSeleccionado && (
        <div className="modal-overlay" onClick={() => setModalVerAbierto(false)}>
          <div className="modal-content modal-presupuesto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalle del Presupuesto</h2>
              <button className="modal-close" onClick={() => setModalVerAbierto(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {/* Información del cliente */}
              <div className="presupuesto-info">
                <div className="info-row">
                  <strong>Cliente:</strong> {presupuestoSeleccionado.cliente?.nombre || '-'}
                </div>
                <div className="info-row">
                  <strong>Teléfono:</strong> {presupuestoSeleccionado.cliente?.telefono || '-'}
                </div>
                <div className="info-row">
                  <strong>Sucursal:</strong> {presupuestoSeleccionado.sucursal?.toUpperCase() || '-'}
                </div>
                <div className="info-row">
                  <strong>Forma de Pago:</strong> {presupuestoSeleccionado.formaPago?.replace('_', ' ').toUpperCase() || '-'}
                </div>
                <div className="info-row">
                  <strong>Estado:</strong> <span className={`estado-badge estado-${presupuestoSeleccionado.estado}`}>
                    {presupuestoSeleccionado.estado?.toUpperCase()}
                  </span>
                </div>
                <div className="info-row">
                  <strong>Fecha:</strong> {new Date(presupuestoSeleccionado.createdAt).toLocaleDateString()}
                </div>
              </div>

              {/* Productos */}
              <div className="productos-agregados">
                <h3>Productos</h3>
                
                {(() => {
                  // Agrupar productos por categoría
                  const productosPorCategoria = {};
                  presupuestoSeleccionado.productos?.forEach(prod => {
                    const categoria = prod.varianteSeleccionada?.categoria || prod.catalogoRef?.categoria || prod.categoria || 'OTROS';
                    if (!productosPorCategoria[categoria]) {
                      productosPorCategoria[categoria] = [];
                    }
                    productosPorCategoria[categoria].push(prod);
                  });

                  const ordenCategorias = ['MUEBLES', 'SOFA', 'SILLA', 'RESPALDO', 'DECORACION', 'ESPECIAL', 'ADICIONALES'];
                  
                  return ordenCategorias.map(categoria => {
                    const productos = productosPorCategoria[categoria];
                    if (!productos || productos.length === 0) return null;

                    return (
                      <div key={categoria} style={{ marginBottom: '30px' }}>
                        <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>{categoria}</h4>
                        <div className="tabla-scroll">
                        <table>
                          <thead>
                            <tr>
                              {categoria === 'MUEBLES' && (
                                <>
                                  <th>TIPO</th>
                                  <th>MODELO</th>
                                  <th>MEDIDAS</th>
                                  <th>MADERA</th>
                                  <th>LUSTRE</th>
                                  <th>COLOR</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                              {categoria === 'SOFA' && (
                                <>
                                  <th>TIPO</th>
                                  <th>MODELO</th>
                                  <th>MEDIDAS</th>
                                  <th>CANT. TAPIZADO</th>
                                  <th>MATERIAL</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                              {categoria === 'SILLA' && (
                                <>
                                  <th>TIPO</th>
                                  <th>MODELO</th>
                                  <th>MADERA</th>
                                  <th>TAPIZADO</th>
                                  <th>CANT. TAPIZADO</th>
                                  <th>LUSTRE</th>
                                  <th>COLOR</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                              {categoria === 'RESPALDO' && (
                                <>
                                  <th>TIPO</th>
                                  <th>MODELO</th>
                                  <th>MEDIDAS</th>
                                  <th>MADERA</th>
                                  <th>TAPIZADO</th>
                                  <th>CANT. TAPIZADO</th>
                                  <th>LUSTRE</th>
                                  <th>COLOR</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                              {categoria === 'DECORACION' && (
                                <>
                                  <th>TIPO</th>
                                  <th>MODELO</th>
                                  <th>SKU</th>
                                  <th>PROVEEDOR</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                              {categoria === 'ADICIONALES' && (
                                <>
                                  <th>NOMBRE</th>
                                  <th>TIPO VALOR</th>
                                  <th>VALOR</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                              {categoria === 'ESPECIAL' && (
                                <>
                                  <th>DESCRIPCIÓN</th>
                                  <th>CANTIDAD</th>
                                  <th>PRECIO UNIT.</th>
                                  <th>SUBTOTAL</th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {productos.map((prod, idx) => {
                              const tipo = prod.varianteSeleccionada?.tipo || prod.catalogoRef?.tipo || prod.tipo || '';
                              const modelo = prod.varianteSeleccionada?.modelo || prod.catalogoRef?.modelo || prod.modelo || '';

                              return (
                                <tr key={idx}>
                                  {categoria === 'MUEBLES' && (
                                    <>
                                      <td>{tipo}</td>
                                      <td>{modelo}</td>
                                      <td>{prod.varianteSeleccionada?.medidas || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.madera || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.lustre || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.color || '-'}</td>
                                      <td>{prod.cantidad}</td>
                                      <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                      <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                    </>
                                  )}
                                  {categoria === 'SOFA' && (
                                    <>
                                      <td>{tipo}</td>
                                      <td>{modelo}</td>
                                      <td>{prod.varianteSeleccionada?.medidas || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.cantidadTapizado || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.lustre || '-'}</td>
                                      <td>{prod.cantidad}</td>
                                      <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                      <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                    </>
                                  )}
                                  {categoria === 'SILLA' && (
                                    <>
                                      <td>{tipo}</td>
                                      <td>{modelo}</td>
                                      <td>{prod.varianteSeleccionada?.madera || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.tapizado || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.cantidadTapizado || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.lustre || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.color || '-'}</td>
                                      <td>{prod.cantidad}</td>
                                      <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                      <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                    </>
                                  )}
                                  {categoria === 'RESPALDO' && (
                                    <>
                                      <td>{tipo}</td>
                                      <td>{modelo}</td>
                                      <td>{prod.varianteSeleccionada?.medidas || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.madera || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.tapizado || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.cantidadTapizado || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.lustre || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.color || '-'}</td>
                                      <td>{prod.cantidad}</td>
                                      <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                      <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                    </>
                                  )}
                                  {categoria === 'DECORACION' && (
                                    <>
                                      <td>{tipo}</td>
                                      <td>{modelo}</td>
                                      <td>{prod.varianteSeleccionada?.sku || '-'}</td>
                                      <td>{prod.varianteSeleccionada?.proveedor || '-'}</td>
                                      <td>{prod.cantidad}</td>
                                      <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                      <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                    </>
                                  )}
                                  {categoria === 'ADICIONALES' && (
                                    <>
                                      <td>{modelo}</td>
                                      <td>{prod.varianteSeleccionada?.tipoValor || '-'}</td>
                                      <td>
                                        {prod.varianteSeleccionada?.tipoValor === 'PORCENTUAL' 
                                          ? `${prod.varianteSeleccionada?.costo || 0}%`
                                          : `$${(prod.varianteSeleccionada?.costo || 0).toLocaleString()}`
                                        }
                                      </td>
                                      <td>{prod.cantidad}</td>
                                      <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                      <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                    </>
                                  )}
                                  {categoria === 'ESPECIAL' && (
                                    <>
                                      <td>{modelo}</td>
                                      <td>{prod.cantidad}</td>
                                      <td>${prod.precioUnitario?.toLocaleString() || 0}</td>
                                      <td>${prod.subtotal?.toLocaleString() || 0}</td>
                                    </>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    );
                  });
                })()}

                <div className="totales">
                  <div className="total-row">
                    <span>Subtotal:</span>
                    <span>${presupuestoSeleccionado.subtotal?.toLocaleString() || 0}</span>
                  </div>
                  {presupuestoSeleccionado.adicionales > 0 && (
                    <div className="total-row">
                      <span>Adicionales (%):</span>
                      <span>${presupuestoSeleccionado.adicionales?.toLocaleString() || 0}</span>
                    </div>
                  )}
                  {presupuestoSeleccionado.descuento > 0 && (
                    <div className="total-row total-row-discount">
                      <span>{formatearEtiquetaAjuste('Bonificacion', presupuestoSeleccionado.descuentoTipo || 'monto', presupuestoSeleccionado.descuentoValor || 0)}</span>
                      <span>-${presupuestoSeleccionado.descuento?.toLocaleString() || 0}</span>
                    </div>
                  )}
                  <div className="total-row">
                    <span>{formatearEtiquetaAjuste('Flete', presupuestoSeleccionado.fleteTipo || 'porcentaje', presupuestoSeleccionado.fleteValor ?? 5)}</span>
                    <span>${presupuestoSeleccionado.flete?.toLocaleString() || 0}</span>
                  </div>
                  <div className="total-row total-final">
                    <span>TOTAL:</span>
                    <span>${presupuestoSeleccionado.total?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>

              {presupuestoSeleccionado.observaciones && (
                <div className="presupuesto-observaciones">
                  <strong>Observaciones:</strong>
                  <p>{presupuestoSeleccionado.observaciones}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setModalVerAbierto(false)}
              >
                Cerrar
              </button>
              {presupuestoSeleccionado.estado === 'pendiente' && (
                <button 
                  className="btn-success" 
                  onClick={() => {
                    setModalVerAbierto(false);
                    handleAceptarPresupuesto(presupuestoSeleccionado._id);
                  }}
                >
                  ✓ Aceptar Presupuesto
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Presupuestos;
